import {
    getAccessToken,
    normalizeCountryCodeForClientSDK,
    normalizeLangToLocaleForServiceCall,
    trackBusinessError,
} from '@sxm-de/dev-kits/web-sdk/utils';
import {
    getOffersNew,
    mapOffersInfoByLocale,
    OffersInfo,
} from '@sxm-de/dev-kits/web-sdk/domains/offers';
import { DomainInitPayload } from '@sxm-de/dev-kits/web-sdk/types';
import { EligibilityOffer } from '../types/eligibility-offer';
import { trackOffersPresented } from '../helpers/track-offers-presented';

interface OfferForPresentmentFnPayload {
    promoCode?: string;
    deviceId?: string;
    province?: string;
    countryCode?: 'us' | 'US' | 'ca' | 'CA';
    serviceType: 'Automotive' | 'Streaming';
}

export type OfferForPresentmentFn = (
    payload: OfferForPresentmentFnPayload,
) => Promise<{
    offer: EligibilityOffer;
    offersInfo: OffersInfo[];
    campaignId: string;
}>;

export function initOfferForPresentmentFn({
    dependencies,
    countryCodeDefault,
    locale,
    state,
}: DomainInitPayload): OfferForPresentmentFn {
    const { appEventSdk, microservicesSdk } = dependencies;
    return async (payload) => {
        const countryCode = normalizeCountryCodeForClientSDK(
            payload.countryCode || countryCodeDefault,
        );

        const { stripePaymentMethodId } = state;

        let offerData;
        try {
            offerData = await getOffersNew(
                {
                    ...(payload.promoCode
                        ? { promoCode: payload.promoCode }
                        : {}),
                    country: normalizeCountryCodeForClientSDK(countryCode),
                    serviceType: payload.serviceType,
                    ...(stripePaymentMethodId ? { stripePaymentMethodId } : {}),
                },
                dependencies,
            );
        } catch (error: any) {
            /**
             * TODO:
             * Log error to error logger (not analytics).
             */
            throw new Error('SYSTEM');
        }

        if (offerData.promoCodeStatus?.invalid) {
            switch (offerData.promoCodeStatus.invalid.reason) {
                case 'Expired':
                    throw new Error('OFFER_EXPIRED');
                case 'Redeemed':
                    throw new Error('OFFER_REDEEMED');
                case 'Canceled':
                case 'Invalid':
                default:
                    throw new Error('INVALID_OFFER_DATA');
            }
        }
        if (offerData.offers.length > 1) {
            const filteredOffers = offerData.offers.filter(
                (offer: any) => offer.offer.baseTerm === 'Monthly',
            );
            offerData = {
                ...offerData,
                offers: filteredOffers,
            };
        }

        const offer = offerData.offers[0].offer as EligibilityOffer;
        trackOffersPresented(appEventSdk, {
            offers: offerData.offers,
            promoCode: payload.promoCode,
        });

        let offersInfo;
        try {
            const request = {
                offerIds: [offer.id],
                ...(payload.province && countryCode.toLowerCase() === 'ca'
                    ? { province: payload.province }
                    : {}),
                locales: locale.supported.map((locale) =>
                    normalizeLangToLocaleForServiceCall(
                        locale,
                        countryCode,
                        payload.province,
                    ),
                ),
                accessToken: getAccessToken(),
            };
            const data =
                await microservicesSdk.domains.offers.getOffersCommerceInfo(
                    request,
                );
            offersInfo = mapOffersInfoByLocale(data);
            if (offersInfo.length === 0) {
                throw new Error('INVALID_OFFER_DATA');
            }
        } catch (error: any) {
            if (error.errorType === 'BUSINESS') {
                trackBusinessError(appEventSdk, error);
            } else {
                /**
                 * TODO:
                 * Log error to error logger (not analytics).
                 */
            }

            throw new Error('SYSTEM');
        }

        return {
            offer,
            offersInfo,
            campaignId: offerData.offers[0].campaignId,
        };
    };
}
